export default class SnakeGame {
    constructor(
        {
            canvasSelector = "#snake",
            scoreSelector = ".score",
            gameInfoSelector = ".game",
            wallPassingSelector = ".wall-passing",
            controlModeSelector = ".control-mode",
            showGrid = false,
            squareScaling = 13,
            cols = 40,
            rows = 24,
            speed = 75
        } = {}) {
        // DOM
        this.canvasSelector = canvasSelector;
        this.scoreSelector = scoreSelector;
        this.gameInfoSelector = gameInfoSelector;
        this.wallPassingSelector = wallPassingSelector;
        this.controlModeSelector = controlModeSelector;

        // Parameter
        this.showGrid = showGrid;
        this.squareSize = squareScaling * 2;
        this.cols = cols;
        this.rows = rows;
        this.speed = this.calculateSpeed(speed);

        // Event handler binding
        this.bindHandlers();

        // Setup
        this.initializeDOM();
        this.initializeCanvas();
        this.initializeGameState();
        this.initializeButtons();
        document.addEventListener("keydown", this.keyHandler);
    }

    bindHandlers() {
        this.keyHandler = (e) => this.keyDown(e);
        this.wallPassingHandler = (e) => this.toggleWallPassing(e);
        this.controlModeHandler = (e) => this.toggleControlMode(e);
    }

    initializeDOM() {
        this.canvas = document.querySelector(this.canvasSelector);
        this.scoreEl = document.querySelector(this.scoreSelector);
        this.gameInfoEl = document.querySelector(this.gameInfoSelector);
        this.wallPassingBtn = document.querySelector(this.wallPassingSelector);
        this.controlModeBtn = document.querySelector(this.controlModeSelector);
    }

    initializeCanvas() {
        this.canvas.width = this.squareSize * this.cols;
        this.canvas.height = this.squareSize * this.rows;
        this.cellWidth = this.canvas.width / this.cols;
        this.cellHeight = this.canvas.height / this.rows;
        this.ctx = this.canvas.getContext('2d');
    }

    initializeGameState() {
        this.snake = [{
            x: Math.floor(this.cols / 2),
            y: Math.floor(this.rows / 2)
        }];
        this.food = {x: 0, y: 0};
        this.direction = 'RIGHT';
        this.foodCollected = false;
        this.score = 0;
        this.started = false;
        this.wallPassing = false;
        this.advancedControls = false;
        this.running = null;
    }

    initializeButtons() {
        if (this.wallPassingBtn) {
            this.wallPassingBtn.addEventListener("click", this.wallPassingHandler);
            this.updateWallPassingText();
        }
        if (this.controlModeBtn) {
            this.controlModeBtn.addEventListener("click", this.controlModeHandler);
            this.updateControlModeText();
        }
    }

    start() {
        this.placeFood();
        this.draw();
    }

    end() {
        clearInterval(this.running);
        document.removeEventListener("keydown", this.keyHandler);
        if (this.wallPassingBtn) {
            this.wallPassingBtn.removeEventListener("click", this.wallPassingHandler);
        }
        if (this.controlModeBtn) {
            this.controlModeBtn.removeEventListener("click", this.controlModeHandler);
        }
    }

    updateWallPassingText() {
        const status = this.wallPassing
            ? '<span style="color: limegreen;">On</span>'
            : '<span style="color: red;">Off</span>';
        this.wallPassingBtn.innerHTML = `Wall passing: ${status}`;
    }

    updateControlModeText() {
        const mode = this.advancedControls
            ? '<span style="color: orange;">Advanced</span>'
            : '<span style="color: limegreen;">Simple</span>';
        this.controlModeBtn.innerHTML = `Controls: ${mode}`;
    }

    toggleWallPassing() {
        if (this.running) return;
        this.wallPassing = !this.wallPassing;
        this.updateWallPassingText();
    }

    toggleControlMode() {
        if (this.running) return;
        this.advancedControls = !this.advancedControls;
        this.updateControlModeText();
    }

    draw() {
        const ctx = this.ctx;
        ctx.fillStyle = 'black';
        ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        // Snake
        ctx.fillStyle = 'white';
        this.snake.forEach(element => this.addRect(element.x, element.y));

        // Food
        ctx.fillStyle = 'red';
        this.addRect(this.food.x, this.food.y);

        //Grid Lines
        if (this.showGrid) {
            ctx.fillStyle = '#111'
            for (let i = this.cellWidth; i < this.canvas.width; i += this.cellWidth) {
                ctx.fillRect(i, 0, 1, this.canvas.height);
            }
            for (let i = this.cellHeight; i < this.canvas.height; i += this.cellHeight) {
                ctx.fillRect(0, i, this.canvas.width, 1);
            }
        }

        if (!this.started) {
            ctx.font = '32px monospace';
            ctx.fillStyle = 'lightgreen';
            ctx.fillText('Press any arrow key to start!', 20, 40);
        }

        requestAnimationFrame(() => this.draw());
    }

    addRect(x, y) {
        this.ctx.fillRect(x * this.cellWidth + 1, y * this.cellHeight + 1, this.cellWidth - 2, this.cellHeight - 2);
    }

    gameLoop() {
        if (this.gameOverCheck()) return;
        this.collectFood();
        this.moveSnake();
        this.foodCheck();
    }

    gameOverCheck() {
        const snakeCopy = structuredClone(this.snake);
        this.moveSnake(snakeCopy);
        return this.borderCheck(snakeCopy) || this.selfEatingCheck(snakeCopy);
    }

    borderCheck(snake) {
        const head = snake[0];
        if (this.wallPassing) return false;
        if (head.x < 0 || head.x >= this.cols || head.y < 0 || head.y >= this.rows) {
            this.gameOver();
            return true;
        }
        return false;
    }

    selfEatingCheck(snake) {
        for (let i = 1; i < snake.length; i++) {
            if (snake[0].x === snake[i].x && snake[0].y === snake[i].y) {
                this.gameOver();
                return true;
            }
        }
        return false;
    }

    moveSnake(snake = this.snake) {
        // Move snake body
        for (let i = snake.length - 1; i > 0; i--) {
            snake[i].x = snake[i - 1].x;
            snake[i].y = snake[i - 1].y;
        }

        // Move snake head
        switch (this.direction) {
            case 'LEFT':
                snake[0].x--;
                break;
            case 'UP':
                snake[0].y--;
                break;
            case 'RIGHT':
                snake[0].x++;
                break;
            case 'DOWN':
                snake[0].y++;
                break;
        }
        if (this.wallPassing) {
            if (snake[0].x < 0) snake[0].x = this.cols - 1;
            if (snake[0].x >= this.cols) snake[0].x = 0;
            if (snake[0].y < 0) snake[0].y = this.rows - 1;
            if (snake[0].y >= this.rows) snake[0].y = 0;
        }
    }

    collectFood() {
        if (this.foodCollected) {
            this.snake = [{x: this.snake[0].x, y: this.snake[0].y}, ...this.snake];
            this.foodCollected = false;
        }
    }

    foodCheck() {
        if (this.snake[0].x === this.food.x && this.snake[0].y === this.food.y) {
            this.foodCollected = true;
            this.placeFood();
            this.score++;
            if (this.scoreEl) this.scoreEl.innerHTML = `Score: ${this.score}`;
        }
    }

    keyDown(e) {
        if ([37, 38, 39, 40, 32, 13, 27].includes(e.keyCode)) {
            e.preventDefault();
        }

        if (this.advancedControls) {
            // ... dein advanced code
        } else {
            if (e.keyCode === 37 && this.direction !== 'RIGHT') this.direction = 'LEFT';
            if (e.keyCode === 38 && this.direction !== 'DOWN') this.direction = 'UP';
            if (e.keyCode === 39 && this.direction !== 'LEFT') this.direction = 'RIGHT';
            if (e.keyCode === 40 && this.direction !== 'UP') this.direction = 'DOWN';
        }

        if (!this.started && [13, 32, 37, 38, 39, 40].includes(e.keyCode)) {
            this.direction = 'RIGHT';
            this.running = setInterval(() => this.gameLoop(), this.speed);
            this.gameLoop();
            this.started = true;
        }

        if (e.keyCode === 27) {
            this.gameOver('Game canceled!');
        }
    }

    placeFood() {
        this.food.x = Math.floor(Math.random() * this.cols);
        this.food.y = Math.floor(Math.random() * this.rows);
    }

    calculateSpeed(speed) {
        const clampedSpeed = Math.min(Math.max(speed, 1), 100);
        const max = 1000;
        const min = 50;
        const t = Math.sqrt(clampedSpeed) / Math.sqrt(100);
        return Math.floor(max - (max - min) * t);
    }

    gameOver(message = 'Game Over!') {
        clearInterval(this.running);
        this.gameInfoEl.innerHTML = message;
    }
}
